/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, MouseEventHandler, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { Checkbox, Row, Col, Dropdown } from "@duxton-web/ui"
import { EditableFilled, MoreVerticalUniversal } from "@duxton-web/icons"
import { Category } from "types/models/menu"
import { useTranslationsMap } from "common/utils/i18n"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import { ConfirmDeleteCategoryOptions } from "utils/category_helper"
import { MenuOverviewRow } from "components/common"
import { availableStatuses, isItemAvailable } from "utils/item_helper"
import { getSellingTimeByID } from "utils/selling_time_helper"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"

interface IProps {
  category: Category
  showEdit?: boolean
}

export const MenuCategoryRow: FC<IProps> = ({ category, showEdit }: IProps) => {
  const dispatch = useDispatch()
  const merchantID = useSelector<AppState, string>(
    state => state?.menu?.merchantID,
  )
  const { isEditAllowed } = useMenuEditorStatus()
  const {
    drawer: { showItemSelection, itemSelections },
    sellingTimes,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  const disableCheckbox = (category.items?.length ?? 0) === 0
  const checkList =
    category.items?.map(item => itemSelections?.[item.itemID ?? ""] ?? false) ??
    []

  const checked = !disableCheckbox && checkList.includes(true)

  const indeterminate = !checkList.every((val, i, arr) => val === arr[0])

  const itemWithoutPhotoCount: number = useMemo(() => {
    // we don't need to find the items without photo if the filter is all items. saving unnecessary calculation here
    if (isItemWithoutPhotoMode(itemFilter)) {
      return getItemWithoutPhotoCount(category.items || [])
    }
    return 0
  }, [category.items])

  const handleCheck = (value: boolean) => {
    dispatch(actionCreator.menu.multiSelectCategory(category, value))
  }

  const confirmDeleteCategoryOptions = ConfirmDeleteCategoryOptions(category)

  const textMap = useTranslationsMap({
    edit: "menu.actions.edit",
    selectAllItems: "menu.overview.selectAllItems",
    delete: "menu.actions.delete",
    deleteSuccess: "menu.notifications.deleteCategory.successMessage",
  })

  const handleClickDelete = async () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickDeleteCategory,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: category.categoryID,
      },
    })
    const resp: any = await dispatch(
      actionCreator.menu.deleteCategory({
        merchantID,
        categoryID: category?.categoryID ?? "",
        confirmOptions: confirmDeleteCategoryOptions,
        successMessage: textMap.deleteSuccess,
        merchantGroupID,
        menuGroupID,
      }),
    )

    if (resp) {
      let labels = {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: category.categoryID,
        ENTRY_POINT: EventEntryPoint.categoryMoreButton,
        STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }

      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.submitDeleteCategory,
        labels,
      })
    }
  }

  const handleMenuClick = (key: string) => {
    switch (key) {
      case "edit":
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewTab,
          action: EventAction.clickEditCategory,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            CATEGORY_ID: category.categoryID,
          },
        })
        dispatch(
          actionCreator.menu.toggleDrawer({
            category,
            showCategory: true,
          }),
        )
        break
      case "all":
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewTab,
          action: EventAction.clickSelectAllCategory,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            CATEGORY_ID: category.categoryID,
          },
        })
        dispatch(
          actionCreator.menu.toggleDrawer({
            showItemSelection: true,
          }),
        )
        handleCheck(true)
        break
      case "delete":
        handleClickDelete()
        break
      default:
        break
    }
  }

  const handleEditClick = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickEditCategory,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: category.categoryID,
      },
    })
    dispatch(
      actionCreator.menu.toggleDrawer({
        category,
        showCategory: true,
      }),
    )
  }

  const countNotAvailable =
    category.items?.reduce((acc, item) => {
      if (item.availableStatus === availableStatuses.HIDDEN_UNTIL_REACTIVATED) {
        return acc
      }
      return acc + (!isItemAvailable(item) ? 1 : 0)
    }, 0) ?? 0

  const activeCategory = useSelector(
    (state: AppState) => state.menu.overview.category,
  )
  const isActiveCategory =
    showEdit && category.categoryID === activeCategory?.categoryID

  const handleClickSelected: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation()
    if (!showItemSelection) {
      dispatch(
        actionCreator.menu.toggleDrawer({
          showItemSelection: true,
        }),
      )
    }
  }

  return (
    <MenuOverviewRow
      name={category.categoryName ?? ""}
      isActive={isActiveCategory}
      isItemWithoutPhotoMode={isItemWithoutPhotoMode(itemFilter)}
      count={
        isItemWithoutPhotoMode(itemFilter)
          ? itemWithoutPhotoCount
          : category?.items?.length ?? 0
      }
      countNotAvailable={countNotAvailable}
      showItemSelection={showItemSelection}
      sellingTime={getSellingTimeByID(category.sellingTimeID, sellingTimes)}
      renderAfter={
        !isItemWithoutPhotoMode(itemFilter) &&
        (isSellingTimeStructure ? (
          showEdit && (
            <Row
              className={`show-hover-container ${
                showItemSelection ? "show" : ""
              }`}
            >
              <Col>
                <Row wrap={false}>
                  <Col className={`editBtn ${showItemSelection ? "" : "show"}`}>
                    <EditableFilled onClick={handleEditClick} />
                  </Col>
                  <Col className="checkbox-container show">
                    <Checkbox
                      disabled={disableCheckbox}
                      indeterminate={indeterminate}
                      defaultChecked={false}
                      checked={checked}
                      onChange={(e: any) => {
                        handleCheck(e.target.checked)
                      }}
                      onClick={handleClickSelected}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          )
        ) : (
          <Row wrap={false}>
            {showEdit && (
              <Col className="show-hover-container">
                <Dropdown
                  className="menuBtn"
                  menu={{
                    items: [
                      {
                        key: "edit",
                        label: textMap.edit,
                        onClick: () => handleMenuClick("edit"),
                      },
                      {
                        key: "all",
                        label: textMap.selectAllItems,
                        onClick: () => handleMenuClick("all"),
                        disabled: category.items?.length === 0,
                      },
                      ...(isEditAllowed
                        ? [
                            {
                              key: "delete",
                              label: textMap.delete,
                              onClick: () => handleMenuClick("delete"),
                              className: "option-error",
                            },
                          ]
                        : []),
                    ],
                  }}
                  trigger={["click"]}
                >
                  <MoreVerticalUniversal />
                </Dropdown>
              </Col>
            )}
            <Col
              className={`checkbox-container ${
                showItemSelection ? "show" : ""
              }`}
            >
              <Checkbox
                disabled={disableCheckbox}
                indeterminate={indeterminate}
                defaultChecked={false}
                checked={checked}
                onChange={(e: any) => {
                  handleCheck(e.target.checked)
                }}
                onClick={handleClickSelected}
              />
            </Col>
          </Row>
        ))
      }
    />
  )
}
